import {
  activeFilters,
  isFilterActive,
  type FilterDef,
  type FilterValue,
  type FilterValues,
  type RangeValue,
} from "./filter-types"

/*
   Filters ↔ URLSearchParams. The convention:

     text          ?assignee=me
     select single ?status=in_review
     select multi  ?submitter=CropStar,Meridian
     boolean       ?hasUnread=1
     date-range    ?updatedAt=2026-08-01..2026-08-25
     number-range  ?findings=2..10

   Same convention the API will accept. So URL sharing, browser history and
   the fetch layer all speak one dialect — no dual-mapping.
*/

const RANGE_SEP = ".."
const LIST_SEP = ","

export function serializeFilters(values: FilterValues): URLSearchParams {
  const params = new URLSearchParams()
  for (const [id, v] of Object.entries(activeFilters(values))) {
    const s = serializeOne(v)
    if (s !== null) params.set(id, s)
  }
  return params
}

function serializeOne(v: FilterValue): string | null {
  if (!isFilterActive(v)) return null
  if (typeof v === "string") return v
  if (typeof v === "boolean") return v ? "1" : "0"
  if (Array.isArray(v)) return v.join(LIST_SEP)
  // `typeof null === "object"` in JS, so a null-valued filter also passes
  // this branch — explicitly exclude null before reading `v.from`/`v.to`.
  if (v !== null && typeof v === "object") {
    const from = v.from == null ? "" : String(v.from)
    const to = v.to == null ? "" : String(v.to)
    return `${from}${RANGE_SEP}${to}`
  }
  return null
}

export function deserializeFilters(params: URLSearchParams, schema: FilterDef[]): FilterValues {
  const out: FilterValues = {}
  for (const def of schema) {
    const raw = params.get(def.id)
    if (raw === null) continue
    const parsed = parseOne(raw, def)
    if (parsed !== undefined) out[def.id] = parsed
  }
  return out
}

function parseOne(raw: string, def: FilterDef): FilterValue | undefined {
  switch (def.kind) {
    case "text":
      return raw
    case "select":
      return def.multi ? raw.split(LIST_SEP).filter(Boolean) : raw
    case "boolean":
      return raw === "1" || raw === "true"
    case "date-range": {
      const range = parseRange(raw)
      return range ? ({ from: range.from, to: range.to } satisfies RangeValue<string>) : undefined
    }
    case "number-range": {
      const range = parseRange(raw)
      if (!range) return undefined
      const from = range.from == null ? null : Number(range.from)
      const to = range.to == null ? null : Number(range.to)
      if ((from != null && Number.isNaN(from)) || (to != null && Number.isNaN(to))) {
        return undefined
      }
      return { from, to } satisfies RangeValue<number>
    }
  }
}

function parseRange(raw: string): { from: string | null; to: string | null } | null {
  const idx = raw.indexOf(RANGE_SEP)
  if (idx === -1) return null
  const from = raw.slice(0, idx)
  const to = raw.slice(idx + RANGE_SEP.length)
  return {
    from: from.length > 0 ? from : null,
    to: to.length > 0 ? to : null,
  }
}
